import { PlayArrow } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import {
  Plus,
  FileText,
  TrendingUp,
  Phone,
  X,
  History,
  Signal,
  Edit2,
  Pause,
} from "lucide-react";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import {
  clearQueueAction,
  gatewaySwitchAction,
  increaseCapacityAction,
  increaseCpsAction,
} from "../ProjectSection/CampaignSection/Store/Campaign.action";
import { useDispatch, useSelector } from "react-redux";
import CpsBody from "./CpsBody";
import CapacityBody from "./CapacityBody";
import GatewayDetails from "../GatewayDetails/GatewayDetails";
import { gatewayConfigStyles } from "../../../assets/MUIStyles/gatewayConfig";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import ScheduleModalBody from "./SchedulingBody";
import {
  getConfigGatewayListAction,
  updateWorkflowConfigAction,
} from "../ProjectSection/CallSettings/Store/callSetting.action";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import ErrorPopUp from "../CommonComponents/PopUpCards/ErrorPopUp";
import { GreenSwitch } from "../Common/GreenSwitch";
import moment from "moment";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import useQueryParams from "../../Hooks/useQueryParams";
import {
  getServerDetailsAction,
  pauseGatewayAction,
  pauseServerAction,
} from "./Store/gatewayConfig.action";

const GatewayConfig = () => {
  const dispatch = useDispatch();
  const { mode } = useQueryParams();
  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData, selectedAccount },
  } = reducer;

  const [modal, setModal] = useState("");
  const [cps, setCps] = useState(0);
  const [capacity, setCapacity] = useState("");
  const [loading, setLoading] = useState(false);
  const [activePopup, setActivePopup] = useState(false);
  const [clearloading, setClearLoading] = useState(false);
  const [pendingActiveState, setPendingActiveState] = useState<any>(null);
  const [startTimes, setStartTimes] = useState(Array(7).fill(null));
  const [endTimes, setEndTimes] = useState(Array(7).fill(null));
  const [defaultStartTime, setDefaultStartTime] = useState("09:00");
  const [defaultEndTime, setDefaultEndTime] = useState("17:00");
  const [isAllDaySame, setIsAllDaySame] = useState(true);
  const [gatewayList, setGatewayList] = useState<any>([]);
  const [selectedGateway, setSelectedGateway] = useState<any>(null);
  const [spin, setSpin] = useState(false);
  const [didValues, setDidValues] = useState({
    didRange: "",
    didPbxRange: "",
  });

  const [successState, setSuccessState] = useState({
    open: false,
    msg: "",
    heading: "",
  });
  const [confirmationState, setConfirmationState] = useState({
    open: false,
    type: "",
    message: "",
    heading: "",
  });
  const [errorObj, setErrorObj] = useState({
    show: false,
    errMsg: "",
  });

  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [serverDetails, setServerDetails] = useState<any>(null);
  const [serverLoading, setServerLoading] = useState(false);

  const fetchGatewayList = async (ifLoad, ifRefresh = false) => {
    if (ifLoad) setLoading(true);
    if (ifRefresh) setRefreshLoading(true);
    const res: any = await dispatch(getConfigGatewayListAction());
    if (res && res?.length > 0) {
      const sortedData = res?.sort((a: any, b: any) => {
        const queueA = a?.queue_size || 0;
        const queueB = b?.queue_size || 0;
        return queueB - queueA;
      });
      setGatewayList(sortedData);
    }
    setLoading(false);
    setRefreshLoading(false);
  };

  useEffect(() => {
    fetchGatewayList(true);
    const intervalTime = mode === "live" ? 2000 : 10000;
    const fetchInterval = setInterval(
      () => fetchGatewayList(false),
      intervalTime
    );
    return () => clearTimeout(fetchInterval);
  }, []);

  useEffect(() => {
    if (!shouldRefetch) return;
    const timer = setTimeout(() => {
      fetchGatewayList(false);
      setShouldRefetch(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [shouldRefetch]);

  const showSuccessMessage = (message: string, heading = "Success") => {
    setSuccessState({
      open: true,
      msg: message,
      heading: heading,
    });
  };

  const showErrorMessage = (message: string) => {
    setErrorObj({
      show: true,
      errMsg: message,
    });
  };

  const showConfirmation = (
    type: string,
    message: string,
    heading = "Confirm"
  ) => {
    setConfirmationState({
      open: true,
      type: type,
      message: message,
      heading: heading,
    });
  };

  const handleConfirmationSave = async () => {
    const { type } = confirmationState;

    try {
      if (type === "queue") {
        await clearQueue();
      } else if (type === "cps") {
        await handleCpsSave();
      } else if (type === "capacity") {
        await handleCapacitySave();
      } else if (type === "schedule") {
        await handleSaveTime();
      } else if (type === "pause_gateway") {
        await handlePauseGateway();
      } else if (type === "pause_server") {
        await handlePauseServer();
      }
    } catch (error) {
      showErrorMessage("An error occurred");
    } finally {
      setConfirmationState({ open: false, type: "", message: "", heading: "" });
    }
  };

  const handleCpsSave = async () => {
    let obj = { cps: Number(cps) };
    setSpin(true);
    try {
      const res: any = await dispatch(
        increaseCpsAction(obj, selectedGateway?.gateway_name)
      );

      if (res?.status) {
        setShouldRefetch(true);
        showSuccessMessage(res?.status);
        setModal("");
        setSelectedGateway(null);
      } else {
        showErrorMessage(res?.errMsg);
        setModal("");
        setSelectedGateway(null);
      }
    } catch (error) {
      showErrorMessage("Failed to increase CPS");
    }
    setSpin(false);
  };

  const handleCapacitySave = async () => {
    let obj = { capacity: capacity };
    setSpin(true);
    try {
      const res: any = await dispatch(
        increaseCapacityAction(obj, selectedGateway?.gateway_name)
      );

      if (res?.status) {
        setShouldRefetch(true);
        showSuccessMessage(res?.status);
        setModal("");
        setSelectedGateway(null);
      } else {
        showErrorMessage(res?.errMsg);
      }
    } catch (error) {
      showErrorMessage("Failed to increase capacity");
    }
    setSpin(false);
  };

  const handleSwitchChange = (event, rowData) => {
    const newState = event.target.checked;
    setSelectedGateway(rowData);
    setPendingActiveState(newState);
    setActivePopup(true);
  };

  const handleGatewayToggle = async () => {
    const actionKey = pendingActiveState ? "enable" : "disable";
    setSpin(true);
    try {
      const res: any = await dispatch(
        gatewaySwitchAction(actionKey, selectedGateway?.gateway_name)
      );
      if (res?.status === 200) {
        await fetchGatewayList(false);
        showSuccessMessage(
          `Gateway ${
            pendingActiveState ? "activated" : "deactivated"
          } successfully`
        );
      }
    } catch (error) {
      showErrorMessage("Failed to toggle gateway status");
    }

    setActivePopup(false);
    setPendingActiveState(null);
    setSelectedGateway(null);
    setSpin(false);
  };

  const handlePauseGateway = async () => {
    setSpin(true);
    const obj = {
      gateway_name: selectedGateway?.gateway_name,
      Dburl: selectedAccount?.accountUrl,
      pause: selectedGateway?.paused
        ? selectedGateway?.paused === "no"
          ? true
          : false
        : true,
    };

    const res: any = await dispatch(
      pauseGatewayAction(selectedGateway?.gateway_name, obj)
    );

    if (res?.status === 200) {
      showSuccessMessage(
        `Gateway ${selectedGateway?.gateway_name} paused successfully`
      );
      await fetchGatewayList(false);
      setSelectedGateway(null);
    } else {
      showErrorMessage(res?.data?.detail || "Failed to pause gateway");
    }

    setSpin(false);
  };

  const openPauseServerModal = async () => {
    setModal("pauseServer");
    setServerDetails(null);
    setServerLoading(true);
    try {
      const params = {
        Dburl: selectedAccount?.accountUrl,
      };
      const res: any = await dispatch(getServerDetailsAction(params));
      setServerDetails(res || null);
    } catch (err) {
      setServerDetails(null);
      showErrorMessage("Failed to fetch service details");
    }
    setServerLoading(false);
  };

  const handlePauseServer = async () => {
    setSpin(true);
    const params = {
      Dburl: selectedAccount?.accountUrl,
      pause: !serverDetails?.paused || false,
    };
    const res: any = await dispatch(pauseServerAction(params));
    if (res?.status === 200) {
      if (serverDetails?.paused) {
        showSuccessMessage("Server restarted successfully", "Restart Server");
      } else {
        showSuccessMessage("Server paused successfully", "Pause Server");
      }
      await fetchGatewayList(false);
      setModal("");
      setServerDetails(null);
    } else {
      showErrorMessage(res?.message || "Failed to pause server");
    }

    setSpin(false);
  };

  const clearQueue = async () => {
    const obj = {
      gateway_name: selectedGateway?.value || selectedGateway?.gateway_name,
      disable: false,
    };
    setClearLoading(true);
    try {
      const res: any = await dispatch(clearQueueAction(obj));
      if (res?.status === 200) {
        showSuccessMessage("Queue cleared successfully");
        await fetchGatewayList(false);
      }
    } catch (error) {
      showErrorMessage("An unexpected error occurred.");
    }
    setClearLoading(false);
  };

  const validateCustomSchedule = () => {
    const dayNames = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const invalidDayIndex = startTimes.findIndex((startTime, index) => {
      if (startTime && endTimes[index]) {
        const start = moment(startTime);
        const end = moment(endTimes[index]);
        return end.isSameOrBefore(start);
      }
      return false;
    });

    if (invalidDayIndex !== -1) {
      showErrorMessage(
        `End time must be after start time for ${dayNames[invalidDayIndex]}`
      );
      return false;
    }

    const hasValidDay = startTimes.some(
      (startTime, index) => startTime && endTimes[index]
    );

    if (!hasValidDay) {
      showErrorMessage(
        "Please set at least one day with both start and end times"
      );
      return false;
    }

    return true;
  };

  const handleSaveTime = async () => {
    if (isAllDaySame) {
      const start = moment(defaultStartTime, "HH:mm");
      const end = moment(defaultEndTime, "HH:mm");

      if (end.isSameOrBefore(start)) {
        showErrorMessage("End time must be after start time");
        return;
      }
    } else {
      if (!validateCustomSchedule()) {
        return;
      }
    }

    const payload = {
      ...selectedGateway,
      start_times: startTimes,
      end_times: endTimes,
      params: {
        all: isAllDaySame,
      },
    };

    setSpin(true);
    let res: any = await dispatch(updateWorkflowConfigAction(payload));

    if (res.message === "updated") {
      await fetchGatewayList(false);
      showSuccessMessage("Gateway schedule updated successfully");
    } else {
      showErrorMessage("Error Updating the Schedule Timing.");
    }
    setModal("");
    setSpin(false);
    setSelectedGateway(null);
  };

  const handleScheduleModalClose = () => {
    setModal("");
    setSelectedGateway(null);
    setStartTimes(Array(7).fill(null));
    setEndTimes(Array(7).fill(null));
    setDefaultStartTime("09:00");
    setDefaultEndTime("17:00");
    setIsAllDaySame(true);
  };

  const handleDidModalClose = () => {
    setModal("");
    setSelectedGateway(null);
    setDidValues({
      didRange: "",
      didPbxRange: "",
    });
  };

  const bodyObj = {
    cps,
    setCps,
    capacity,
    setCapacity,
    selectedGateway,
    setSelectedGateway,
  };

  const timeObj = {
    startTimes,
    setStartTimes,
    endTimes,
    setEndTimes,
    setDefaultEndTime,
    setDefaultStartTime,
    defaultEndTime,
    defaultStartTime,
    isAllDaySame,
    setIsAllDaySame,
  };

  const handleDidInputChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name === "didRange" ? "didRange" : "didPbxRange";
    setDidValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleDidSave = async () => {
    const payload = {
      ...selectedGateway,
      did_range:
        modal === "didRange" ? didValues.didRange : selectedGateway?.did_range,
      pbx_did_range:
        modal === "pbxDidRange"
          ? didValues.didPbxRange
          : selectedGateway?.pbx_did_range,
    };

    setSpin(true);
    let res: any = await dispatch(updateWorkflowConfigAction(payload));
    if (res.message === "updated") {
      await fetchGatewayList(false);
      showSuccessMessage("DID range updated successfully");
    } else {
      showErrorMessage("Error Upating the DID");
    }
    handleDidModalClose();
    setSpin(false);
  };

  const gatewayName = (gateway) => {
    const selectedGateway = constantData?.gateways?.find(
      (gw) => gw?.value === gateway?.gateway_name
    );
    return selectedGateway?.name;
  };

  return (
    <Box className="my-1">
      <Box className="flex-between" gap={1}>
        <Typography className="font-20-grey mb-2">
          Gateway Configuration
        </Typography>
        <Box className="flex-xy-center gap-2">
          <Button
            size="small"
            variant="outlined"
            onClick={openPauseServerModal}
          >
            Pause/Restart Server
          </Button>
          <IconButton
            title="Refresh"
            onClick={() => fetchGatewayList(false, true)}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
            disabled={loading}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
        </Box>
      </Box>

      <Box className="my-2">
        <DynamicTable
          columns={[
            {
              label: "Gateway",
              render: (row) => (
                <Box
                  sx={{
                    bgcolor: row?.queue_size > 0 ? "#adfaadce" : "transparent",
                    py: 1.5,
                    px: 2,
                    borderRadius: 0.5,
                  }}
                >
                  <strong>{gatewayName(row)}</strong>
                  <br />
                  <span style={{ color: "#666" }}>
                    {row?.gateway_name || "--"}
                  </span>
                </Box>
              ),
              styles: { py: "4px", px: "2px" },
            },
            {
              label: "Active",
              render: (row) => {
                return (
                  <Box className="flex-xy-center">
                    <GreenSwitch
                      checked={row?.active === "yes"}
                      onChange={(event) => handleSwitchChange(event, row)}
                    />
                  </Box>
                );
              },
            },
            {
              label: "Queue",
              render: (row) => {
                const hasQueue = row?.queue_size;

                return hasQueue ? (
                  <Box className="flex-xy-center">
                    <Box className="flex-align-center" gap={1}>
                      <Typography className="font-12-mediumgrey">
                        {row?.queue_size}
                      </Typography>
                      <IconButton
                        title="Clear Queue"
                        size="small"
                        onClick={() => {
                          setSelectedGateway(row);
                          showConfirmation(
                            "queue",
                            `Do you want to clear the queue for ${row?.gateway_name}?`,
                            "Clear Queue"
                          );
                        }}
                        sx={{
                          color: "#ff6b6b",
                          "&:hover": {
                            backgroundColor: "rgba(255, 107, 107, 0.1)",
                          },
                        }}
                      >
                        <X size={16} color="#ff6b6b" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Typography className="font-12-mediumgrey">0</Typography>
                );
              },
            },
            {
              label: "Used",
              render: (row) => (row?.used !== null ? row?.used : "--"),
            },
            {
              label: "Scheduled",
              render: (row) =>
                row?.scheduled !== null ? row?.scheduled : "--",
            },
            {
              label: "CPS",
              render: (row) => (row?.cps !== null ? row?.cps : "--"),
            },
            {
              label: "Capacity",
              render: (row) => (row?.capacity !== null ? row?.capacity : "--"),
            },
            {
              label: "Answered",
              render: (row) => (row?.answered !== null ? row?.answered : "--"),
            },
            {
              label: "Did Range",
              render: (row) => {
                return (
                  <Box className="flex-xy-center" gap={0.5}>
                    <Typography className="font-12-mediumgrey">
                      {row?.did_range}
                    </Typography>
                    <IconButton
                      title="Set Did Range"
                      onClick={() => {
                        setSelectedGateway(row);
                        setModal("didRange");
                        setDidValues((prev) => ({
                          ...prev,
                          didRange: row?.did_range || "",
                        }));
                      }}
                      disabled={false}
                      sx={gatewayConfigStyles.commonIconButton}
                    >
                      <Edit2 size={12} />
                    </IconButton>
                  </Box>
                );
              },
            },
            {
              label: "PBX Did Range",
              render: (row) => {
                return row?.pbx_did_range !== null ? (
                  <Box className="flex-xy-center" gap={0.5}>
                    <Typography className="font-12-mediumgrey">
                      {row?.pbx_did_range}
                    </Typography>
                  </Box>
                ) : (
                  "--"
                );
              },
            },
            {
              label: "ACTIONS",
              render: (row) => {
                return (
                  <Box>
                    {row?.paused && row?.paused !== "no" ? (
                      <IconButton
                        title="Resume Gateway"
                        onClick={() => {
                          setSelectedGateway(row);
                          showConfirmation(
                            "pause_gateway",
                            `Do you want to resume ${gatewayName(row)} ?`,
                            "Resume Gateway"
                          );
                        }}
                        disabled={false}
                        sx={gatewayConfigStyles.commonIconButton}
                      >
                        <PlayArrow />
                      </IconButton>
                    ) : (
                      <IconButton
                        title="Pause Gateway"
                        onClick={() => {
                          setSelectedGateway(row);
                          showConfirmation(
                            "pause_gateway",
                            `Do you want to pause ${gatewayName(row)} ?`,
                            "Pause Gateway"
                          );
                        }}
                        disabled={false}
                        sx={gatewayConfigStyles.commonIconButton}
                      >
                        <Pause size={16} />
                      </IconButton>
                    )}

                    <IconButton
                      title="Gateway Summary"
                      onClick={() => {
                        setSelectedGateway(row);
                        setModal("summary");
                      }}
                      disabled={false}
                    >
                      <FileText size={16} />
                    </IconButton>
                    <IconButton
                      title="Increase CPS"
                      onClick={() => {
                        setSelectedGateway(row);
                        setModal("cps");
                        setCps(row?.cps);
                      }}
                      disabled={false}
                      sx={gatewayConfigStyles.commonIconButton}
                    >
                      <Box
                        className="flex-xy-center"
                        sx={gatewayConfigStyles.iconContainer}
                      >
                        <Phone size={16} />
                        <TrendingUp
                          size={12}
                          style={
                            gatewayConfigStyles.trendingIcon as React.CSSProperties
                          }
                        />
                      </Box>
                    </IconButton>
                    <IconButton
                      title="Increase Capacity"
                      onClick={() => {
                        setModal("capacity");
                        setSelectedGateway(row);
                        setCapacity(row?.capacity);
                      }}
                      disabled={false}
                      sx={gatewayConfigStyles.commonIconButton}
                    >
                      <Box
                        className="flex-xy-center"
                        sx={gatewayConfigStyles.iconContainer}
                      >
                        <Phone size={16} />

                        <Plus
                          size={12}
                          style={
                            gatewayConfigStyles.plusIcon as React.CSSProperties
                          }
                        />
                      </Box>
                    </IconButton>
                    <IconButton
                      title="Gateway Schedule"
                      onClick={() => {
                        setSelectedGateway(row);
                        if (row?.start_times && row?.end_times) {
                          setStartTimes([...row.start_times]);
                          setEndTimes([...row.end_times]);
                        } else {
                          setStartTimes(Array(7).fill(null));
                          setEndTimes(Array(7).fill(null));
                        }

                        setIsAllDaySame(row?.params?.all ?? true);
                        setModal("schedule");
                      }}
                      disabled={false}
                      sx={gatewayConfigStyles.commonIconButton}
                    >
                      <Box
                        className="flex-xy-center"
                        sx={gatewayConfigStyles.iconContainer}
                      >
                        <Signal size={16} />

                        <History
                          size={12}
                          style={
                            gatewayConfigStyles.plusIcon as React.CSSProperties
                          }
                        />
                      </Box>
                    </IconButton>
                  </Box>
                );
              },
            },
          ]}
          data={gatewayList}
          loading={loading}
          totalPages={0}
          currentPage={0}
          handlePageChange={() => {}}
          handlePrevious={() => {}}
          handleNext={() => {}}
          noDataAvailableMessage="No Gateway Config. Data Available"
          rowCount={gatewayList?.length}
        />
      </Box>

      {modal === "summary" && (
        <CommonModal
          body={
            <GatewayDetails
              gatewayFromConfig={selectedGateway?.gateway_name}
              fromConfig={true}
            />
          }
          handleSave={() => {}}
          heading={`Gateway Summary`}
          show={modal === "summary" ? true : false}
          size={800}
          toggle={() => {
            setModal("");
            setSelectedGateway(null);
          }}
          spin={false}
          showSaveButton={false}
        />
      )}
      {modal === "cps" && (
        <CommonModal
          body={<CpsBody obj={bodyObj} />}
          handleSave={() =>
            showConfirmation(
              "cps",
              `Do you want to increase CPS for ${selectedGateway?.gateway_name}?`,
              "Increase CPS"
            )
          }
          heading={`Increase CPS`}
          show={modal === "cps" ? true : false}
          size={600}
          toggle={() => {
            setModal("");
            setSelectedGateway(null);
            setCps(0);
          }}
          spin={spin}
        />
      )}
      {modal === "capacity" && (
        <CommonModal
          body={<CapacityBody obj={bodyObj} />}
          handleSave={() =>
            showConfirmation(
              "capacity",
              `Do you want to increase capacity for ${selectedGateway?.gateway_name}?`,
              "Increase Capacity"
            )
          }
          heading={`Increase Capacity`}
          show={modal === "capacity" ? true : false}
          size={600}
          toggle={() => {
            setModal("");
            setSelectedGateway(null);
            setCapacity("");
          }}
          spin={spin}
        />
      )}
      {modal === "schedule" && (
        <CommonModal
          body={<ScheduleModalBody obj={timeObj} />}
          handleSave={() =>
            showConfirmation(
              "schedule",
              `Do you want to update the schedule for ${selectedGateway?.gateway_name}?`,
              "Update Schedule"
            )
          }
          heading={`Gateway Configuration`}
          show={modal === "schedule" ? true : false}
          size={600}
          toggle={handleScheduleModalClose}
          spin={spin}
        />
      )}

      {modal === "pauseServer" && (
        <CommonModal
          body={
            <Box>
              <Typography className="font-14-600-grey" sx={{ mb: 1 }}>
                Server Details
              </Typography>

              {serverLoading ? (
                <Typography className="font-12-mediumgrey">
                  Loading...
                </Typography>
              ) : serverDetails ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography className="font-12-mediumgrey">
                    Status:{" "}
                    <strong>
                      {serverDetails?.paused ? "Paused" : "Running"}
                    </strong>
                  </Typography>
                </Box>
              ) : (
                <Typography className="font-12-mediumgrey">
                  Could not fetch service details.
                </Typography>
              )}
            </Box>
          }
          handleSave={() => {
            if (serverDetails?.paused) {
              showConfirmation(
                "pause_server",
                "Are you sure you want to restart the server?",
                "Restart Server"
              );
            } else {
              showConfirmation(
                "pause_server",
                "Are you sure you want to pause the server?",
                "Pause Server"
              );
            }
          }}
          heading={serverDetails?.paused ? "Restart Server" : "Pause Server"}
          show={modal === "pauseServer"}
          size={600}
          toggle={() => {
            setModal("");
            setServerDetails(null);
          }}
          spin={spin}
          saveButtonText={
            serverDetails?.paused ? "Restart Server" : "Pause Server"
          }
          disableSaveButton={!serverDetails}
        />
      )}

      {(modal === "didRange" || modal === "pbxDidRange") && (
        <CommonModal
          body={
            <TextField
              name={modal}
              value={
                modal === "didRange"
                  ? didValues.didRange
                  : didValues.didPbxRange
              }
              onChange={handleDidInputChange}
              fullWidth
              sx={commonStyles.inputField}
            />
          }
          handleSave={handleDidSave}
          heading={`Set ${modal === "didRange" ? "DID" : "DID PBX"} Range`}
          show={true}
          size={600}
          toggle={handleDidModalClose}
          spin={spin}
        />
      )}

      {activePopup && (
        <ConfirmationPopup
          handleSave={handleGatewayToggle}
          heading={"Confirm"}
          message={`Do you want to ${
            pendingActiveState ? "activate" : "deactivate"
          } the Gateway?`}
          show={activePopup}
          toggle={() => {
            setActivePopup(false);
            setPendingActiveState(null);
          }}
          Icon={ConfirmationIcon}
          spin={spin}
        />
      )}

      {confirmationState.open && (
        <ConfirmationPopup
          handleSave={handleConfirmationSave}
          heading={confirmationState.heading}
          message={confirmationState.message}
          show={confirmationState.open}
          toggle={() =>
            setConfirmationState({
              open: false,
              type: "",
              message: "",
              heading: "",
            })
          }
          Icon={ConfirmationIcon}
          spin={spin}
        />
      )}

      {successState?.open && (
        <CommonPopup
          message={successState.msg}
          show={successState?.open}
          toggle={() => setSuccessState({ open: false, msg: "", heading: "" })}
          Icon={SuccessIcon}
          heading={successState?.heading}
        />
      )}

      <ErrorPopUp
        show={errorObj.show}
        toggle={() =>
          setErrorObj((prev) => ({
            ...prev,
            show: false,
            errMsg: "",
          }))
        }
        heading={"Error"}
        message={errorObj?.errMsg}
        spin={spin}
      />
    </Box>
  );
};

export default GatewayConfig;
